/**
 * REDEX Attention Engine - Dashboard Page
 * 
 * Global Map: Visual representation of the demand capture landscape
 * Ontology Inspector: Build and manage intent hierarchies
 * Page Matrix: Performance tracking for programmatic pages
 * Capital Allocation Panel: Kelly-optimized resource distribution
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  TextField,
  Chip,
  useTheme,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Snackbar,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Card,
  CardContent,
  CardHeader,
  CardActions,
  IconButton,
  InputAdornment,
  Divider,
  Tab,
  Tabs,
  Stack,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Collapse,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  FormControlLabel,
  Slider,
  Badge,
  Avatar,
  AvatarGroup,
  ToggleButton,
  ToggleButtonGroup,
  Breadcrumbs,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Fab,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridRowSelectionModel,
} from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  Dashboard as DashboardIcon,
  AccountTree as OntologyIcon,
  GridView as MatrixIcon,
  AttachMoney as MoneyIcon,
  Security as ComplianceIcon,
  ContentCopy as ContentIcon,
  Speed as SpeedIcon,
  Visibility as VisibilityIcon,
  PlayArrow as PublishIcon,
  Pause as PauseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  Circle as CircleIcon,
  Star as StarIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Public as PublicIcon,
  Business as BusinessIcon,
  Category as CategoryIcon,
  LocationOn as LocationIcon,
  Psychology as PsychologyIcon,
  AutoGraph as AutoGraphIcon,
  Lightbulb as LightbulbIcon,
  ExpandLess,
  ExpandMore,
} from '@mui/icons-material';
import {
  attentionEngineService,
  Industry,
  OntologyItem,
  Page,
  DashboardData,
  PageMatrixItem,
  Allocation,
  CoverageGap,
  WinningPattern,
} from '../services/attentionEngineService';

// ==========================================
// TAB PANEL
// ==========================================

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
  </div>
);

// ==========================================
// MAIN COMPONENT
// ==========================================

const AttentionEnginePage: React.FC = () => {
  const theme = useTheme();

  // Navigation
  const [activeTab, setActiveTab] = useState(0);
  const [selectedIndustry, setSelectedIndustry] = useState<Industry | null>(null);

  // Data
  const [industries, setIndustries] = useState<Industry[]>([]);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [ontologyItems, setOntologyItems] = useState<OntologyItem[]>([]);
  const [pages, setPages] = useState<PageMatrixItem[]>([]);
  const [allocations, setAllocations] = useState<Allocation[]>([]);
  const [gaps, setGaps] = useState<CoverageGap[]>([]);
  const [patterns, setPatterns] = useState<WinningPattern[]>([]);

  // UI State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Dialogs
  const [industryDialog, setIndustryDialog] = useState(false);
  const [ontologyDialog, setOntologyDialog] = useState(false);
  const [pageDialog, setPageDialog] = useState(false);
  const [allocationDialog, setAllocationDialog] = useState(false);

  // Form data
  const [newIndustry, setNewIndustry] = useState({ name: '', description: '', isRegulated: false });
  const [newOntologyItem, setNewOntologyItem] = useState({ type: 'primitive' as const, slug: '', name: '', parentId: '' });
  const [allocationBudget, setAllocationBudget] = useState(1000);

  // ==========================================
  // DATA LOADING
  // ==========================================

  const loadDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const [industriesData, dashboard] = await Promise.all([
        attentionEngineService.getIndustries(),
        attentionEngineService.getDashboardData(),
      ]);
      setIndustries(industriesData);
      setDashboardData(dashboard);
      
      if (industriesData.length > 0 && !selectedIndustry) {
        setSelectedIndustry(industriesData[0]);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [selectedIndustry]);

  const loadIndustryData = useCallback(async () => {
    if (!selectedIndustry) return;
    
    try {
      setLoading(true);
      const [ontology, pagesData, allocs, gapsData, patternsData] = await Promise.all([
        attentionEngineService.getOntologyTree(selectedIndustry.id),
        attentionEngineService.getPageMatrix(selectedIndustry.id, { limit: 100 }),
        attentionEngineService.getAllocations(selectedIndustry.id),
        attentionEngineService.analyzeCoverageGaps(selectedIndustry.id),
        attentionEngineService.getWinningPatterns(selectedIndustry.id, 10),
      ]);
      setOntologyItems(ontology);
      setPages(pagesData.pages);
      setAllocations(allocs);
      setGaps(gapsData);
      setPatterns(patternsData);
    } catch (err: any) {
      setError(err.message || 'Failed to load industry data');
    } finally {
      setLoading(false);
    }
  }, [selectedIndustry]);

  useEffect(() => {
    loadDashboard();
  }, []);

  useEffect(() => {
    if (selectedIndustry) {
      loadIndustryData();
    }
  }, [selectedIndustry]);

  // ==========================================
  // ACTIONS
  // ==========================================

  const handleCreateIndustry = async () => {
    try {
      const industry = await attentionEngineService.createIndustry(newIndustry);
      setIndustries([...industries, industry]);
      setSelectedIndustry(industry);
      setIndustryDialog(false);
      setNewIndustry({ name: '', description: '', isRegulated: false });
      setSuccess('Industry created successfully');
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleCreateOntologyItem = async () => {
    if (!selectedIndustry) return;
    try {
      await attentionEngineService.createOntologyItem({
        industryId: selectedIndustry.id,
        type: newOntologyItem.type,
        slug: newOntologyItem.slug,
        name: newOntologyItem.name,
        parentId: newOntologyItem.parentId || undefined,
      } as any);
      await loadIndustryData();
      setOntologyDialog(false);
      setNewOntologyItem({ type: 'primitive', slug: '', name: '', parentId: '' });
      setSuccess('Ontology item created');
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleRunScoringCycle = async () => {
    if (!selectedIndustry) return;
    try {
      setLoading(true);
      const result = await attentionEngineService.runScoringCycle(selectedIndustry.id);
      setSuccess(`Scored ${result.pagesScored} pages. Avg score: ${result.avgScore.toFixed(2)}`);
      await loadIndustryData();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRunAllocation = async () => {
    if (!selectedIndustry) return;
    try {
      setLoading(true);
      const newAllocations = await attentionEngineService.runAllocationCycle(
        selectedIndustry.id,
        allocationBudget
      );
      setAllocations([...allocations, ...newAllocations]);
      setAllocationDialog(false);
      setSuccess(`Created ${newAllocations.length} allocations`);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateCombinations = async () => {
    if (!selectedIndustry) return;
    try {
      setLoading(true);
      const combinations = await attentionEngineService.generateIntentCombinations(selectedIndustry.id);
      setSuccess(`Generated ${combinations.length} intent combinations`);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ==========================================
  // RENDER HELPERS
  // ==========================================

  const getTrendIcon = (trend: string | number) => {
    if (typeof trend === 'number') {
      if (trend > 0.05) return <TrendingUpIcon color="success" fontSize="small" />;
      if (trend < -0.05) return <TrendingDownIcon color="error" fontSize="small" />;
      return <TrendingFlatIcon color="action" fontSize="small" />;
    }
    if (trend === 'rising') return <TrendingUpIcon color="success" fontSize="small" />;
    if (trend === 'falling') return <TrendingDownIcon color="error" fontSize="small" />;
    return <TrendingFlatIcon color="action" fontSize="small" />;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'published': return 'success';
      case 'draft': return 'default';
      case 'paused': return 'warning';
      case 'archived': return 'error';
      default: return 'default';
    }
  };

  const formatNumber = (n: number) => {
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
    return n.toString();
  };

  // ==========================================
  // GLOBAL MAP TAB
  // ==========================================

  const renderGlobalMap = () => (
    <Grid container spacing={3}>
      {/* Top Stats */}
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.2)} 100%)` }}>
              <CardContent>
                <Typography variant="overline" color="textSecondary">Total Pages</Typography>
                <Typography variant="h3">{formatNumber(dashboardData?.totalPages || 0)}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <MatrixIcon fontSize="small" sx={{ mr: 0.5 }} />
                  <Typography variant="caption">Programmatic surfaces</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)} 0%, ${alpha(theme.palette.info.main, 0.2)} 100%)` }}>
              <CardContent>
                <Typography variant="overline" color="textSecondary">Total Impressions</Typography>
                <Typography variant="h3">{formatNumber(dashboardData?.totalImpressions || 0)}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <VisibilityIcon fontSize="small" sx={{ mr: 0.5 }} />
                  <Typography variant="caption">Attention captured</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.main, 0.2)} 100%)` }}>
              <CardContent>
                <Typography variant="overline" color="textSecondary">Conversions</Typography>
                <Typography variant="h3">{formatNumber(dashboardData?.totalConversions || 0)}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <CheckCircleIcon fontSize="small" sx={{ mr: 0.5 }} />
                  <Typography variant="caption">Demand captured</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.1)} 0%, ${alpha(theme.palette.warning.main, 0.2)} 100%)` }}>
              <CardContent>
                <Typography variant="overline" color="textSecondary">Attention CAGR</Typography>
                <Typography variant="h3">{((dashboardData?.attentionCagr || 0) * 100).toFixed(1)}%</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <AutoGraphIcon fontSize="small" sx={{ mr: 0.5 }} />
                  <Typography variant="caption">Compound growth rate</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Grid>

      {/* Industry Selector */}
      <Grid item xs={12} md={4}>
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Industries</Typography>
            <IconButton size="small" onClick={() => setIndustryDialog(true)}>
              <AddIcon />
            </IconButton>
          </Box>
          <List>
            {industries.map((ind) => (
              <ListItem
                key={ind.id}
                button
                selected={selectedIndustry?.id === ind.id}
                onClick={() => setSelectedIndustry(ind)}
                sx={{ borderRadius: 1, mb: 0.5 }}
              >
                <ListItemIcon>
                  <BusinessIcon color={selectedIndustry?.id === ind.id ? 'primary' : 'inherit'} />
                </ListItemIcon>
                <ListItemText
                  primary={ind.name}
                  secondary={`${dashboardData?.industriesSummary.find(s => s.id === ind.id)?.activePages || 0} pages`}
                />
                {ind.isRegulated && (
                  <Chip size="small" label="Regulated" color="warning" variant="outlined" />
                )}
              </ListItem>
            ))}
            {industries.length === 0 && (
              <ListItem>
                <ListItemText
                  primary="No industries yet"
                  secondary="Create your first industry to get started"
                />
              </ListItem>
            )}
          </List>
        </Paper>
      </Grid>

      {/* Industry Summary */}
      <Grid item xs={12} md={8}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>Industry Performance</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Industry</TableCell>
                  <TableCell align="right">Pages</TableCell>
                  <TableCell align="right">Impressions</TableCell>
                  <TableCell align="right">Conversions</TableCell>
                  <TableCell align="right">Avg Score</TableCell>
                  <TableCell align="center">Trend</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dashboardData?.industriesSummary.map((summary) => (
                  <TableRow key={summary.id} hover>
                    <TableCell>{summary.name}</TableCell>
                    <TableCell align="right">{summary.activePages}</TableCell>
                    <TableCell align="right">{formatNumber(summary.impressions)}</TableCell>
                    <TableCell align="right">{summary.conversions}</TableCell>
                    <TableCell align="right">{summary.avgScore.toFixed(1)}</TableCell>
                    <TableCell align="center">{getTrendIcon(summary.trend)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Grid>

      {/* Coverage Gaps */}
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Coverage Gaps</Typography>
            <Chip label={`${gaps.length} opportunities`} size="small" color="primary" />
          </Box>
          <List dense>
            {gaps.slice(0, 5).map((gap, idx) => (
              <ListItem key={idx}>
                <ListItemIcon>
                  <LightbulbIcon color="warning" />
                </ListItemIcon>
                <ListItemText
                  primary={`${gap.primitive} + ${gap.context}${gap.modifier ? ` + ${gap.modifier}` : ''}`}
                  secondary={`Est. volume: ${gap.estimatedVolume || 'Unknown'} | Competition: ${gap.competition || 'Unknown'}`}
                />
                <ListItemSecondaryAction>
                  <Button size="small" variant="outlined">Generate</Button>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
            {gaps.length === 0 && (
              <ListItem>
                <ListItemText secondary="No coverage gaps detected" />
              </ListItem>
            )}
          </List>
        </Paper>
      </Grid>

      {/* Winning Patterns */}
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>Winning Patterns</Typography>
          <List dense>
            {patterns.slice(0, 5).map((pattern, idx) => (
              <ListItem key={idx}>
                <ListItemIcon>
                  <StarIcon color="success" />
                </ListItemIcon>
                <ListItemText
                  primary={pattern.pattern.join(' → ')}
                  secondary={`${pattern.pageCount} pages | Avg score: ${pattern.avgScore.toFixed(1)} | ${pattern.totalConversions} conversions`}
                />
              </ListItem>
            ))}
            {patterns.length === 0 && (
              <ListItem>
                <ListItemText secondary="No winning patterns identified yet" />
              </ListItem>
            )}
          </List>
        </Paper>
      </Grid>
    </Grid>
  );

  // ==========================================
  // ONTOLOGY TAB
  // ==========================================

  const OntologyTree: React.FC<{ items: OntologyItem[]; depth?: number }> = ({ items, depth = 0 }) => {
    const [expanded, setExpanded] = useState<Set<string>>(new Set());

    const toggle = (id: string) => {
      const newExpanded = new Set(expanded);
      if (newExpanded.has(id)) {
        newExpanded.delete(id);
      } else {
        newExpanded.add(id);
      }
      setExpanded(newExpanded);
    };

    const getTypeIcon = (type: string) => {
      switch (type) {
        case 'primitive': return <CategoryIcon color="primary" />;
        case 'scenario': return <PsychologyIcon color="secondary" />;
        case 'modifier': return <SpeedIcon color="warning" />;
        case 'context': return <LocationIcon color="success" />;
        default: return <CircleIcon />;
      }
    };

    return (
      <List dense sx={{ pl: depth * 2 }}>
        {items.map((item) => (
          <React.Fragment key={item.id}>
            <ListItem button onClick={() => item.children?.length && toggle(item.id)}>
              <ListItemIcon>
                {item.children?.length ? (
                  expanded.has(item.id) ? <ExpandLess /> : <ExpandMore />
                ) : null}
              </ListItemIcon>
              <ListItemIcon>{getTypeIcon(item.type)}</ListItemIcon>
              <ListItemText
                primary={item.name}
                secondary={`${item.type} • ${item.slug}`}
              />
              <Chip size="small" label={item.type} variant="outlined" />
            </ListItem>
            {item.children && expanded.has(item.id) && (
              <Collapse in={expanded.has(item.id)}>
                <OntologyTree items={item.children} depth={depth + 1} />
              </Collapse>
            )}
          </React.Fragment>
        ))}
      </List>
    );
  };

  const renderOntology = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Intent Ontology: {selectedIndustry?.name || 'Select an industry'}
          </Typography>
          <Box>
            <Button
              startIcon={<AutoGraphIcon />}
              onClick={handleGenerateCombinations}
              disabled={!selectedIndustry}
              sx={{ mr: 1 }}
            >
              Generate Combinations
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOntologyDialog(true)}
              disabled={!selectedIndustry}
            >
              Add Item
            </Button>
          </Box>
        </Box>
      </Grid>

      <Grid item xs={12} md={8}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle2" color="textSecondary" gutterBottom>
            Hierarchical Intent Structure
          </Typography>
          {ontologyItems.length > 0 ? (
            <OntologyTree items={ontologyItems} />
          ) : (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <OntologyIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography color="textSecondary">
                No ontology items yet. Add primitives, scenarios, modifiers, and contexts to build your intent hierarchy.
              </Typography>
            </Box>
          )}
        </Paper>
      </Grid>

      <Grid item xs={12} md={4}>
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Quick Add</Typography>
          <Stack spacing={2}>
            {['primitive', 'scenario', 'modifier', 'context'].map((type) => (
              <Button
                key={type}
                variant="outlined"
                fullWidth
                startIcon={
                  type === 'primitive' ? <CategoryIcon /> :
                  type === 'scenario' ? <PsychologyIcon /> :
                  type === 'modifier' ? <SpeedIcon /> :
                  <LocationIcon />
                }
                onClick={() => {
                  setNewOntologyItem({ ...newOntologyItem, type: type as any });
                  setOntologyDialog(true);
                }}
              >
                Add {type.charAt(0).toUpperCase() + type.slice(1)}
              </Button>
            ))}
          </Stack>
        </Paper>

        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Ontology Stats</Typography>
          <List dense>
            <ListItem>
              <ListItemIcon><CategoryIcon color="primary" /></ListItemIcon>
              <ListItemText primary="Primitives" />
              <Typography variant="body2">
                {ontologyItems.filter(i => i.type === 'primitive').length}
              </Typography>
            </ListItem>
            <ListItem>
              <ListItemIcon><PsychologyIcon color="secondary" /></ListItemIcon>
              <ListItemText primary="Scenarios" />
              <Typography variant="body2">
                {ontologyItems.filter(i => i.type === 'scenario').length}
              </Typography>
            </ListItem>
            <ListItem>
              <ListItemIcon><SpeedIcon color="warning" /></ListItemIcon>
              <ListItemText primary="Modifiers" />
              <Typography variant="body2">
                {ontologyItems.filter(i => i.type === 'modifier').length}
              </Typography>
            </ListItem>
            <ListItem>
              <ListItemIcon><LocationIcon color="success" /></ListItemIcon>
              <ListItemText primary="Contexts" />
              <Typography variant="body2">
                {ontologyItems.filter(i => i.type === 'context').length}
              </Typography>
            </ListItem>
          </List>
        </Paper>
      </Grid>
    </Grid>
  );

  // ==========================================
  // PAGE MATRIX TAB
  // ==========================================

  const pageColumns: GridColDef[] = [
    {
      field: 'title',
      headerName: 'Page',
      flex: 2,
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          <Typography variant="body2" fontWeight="medium">{params.row.title}</Typography>
          <Typography variant="caption" color="textSecondary">{params.row.slug}</Typography>
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 100,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value}
          size="small"
          color={getStatusColor(params.value) as any}
        />
      ),
    },
    {
      field: 'score',
      headerName: 'Score',
      width: 100,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography>{params.value?.toFixed(1) || 0}</Typography>
          <Box sx={{ ml: 0.5 }}>{getTrendIcon(params.row.trend)}</Box>
        </Box>
      ),
    },
    { field: 'impressions', headerName: 'Impressions', width: 120, type: 'number' },
    { field: 'conversions', headerName: 'Conversions', width: 120, type: 'number' },
    {
      field: 'ontologyPath',
      headerName: 'Intent Path',
      flex: 1,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {params.value?.slice(0, 3).map((slug: string, idx: number) => (
            <Chip key={idx} label={slug} size="small" variant="outlined" />
          ))}
          {params.value?.length > 3 && <Chip label={`+${params.value.length - 3}`} size="small" />}
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          <IconButton size="small">
            <VisibilityIcon fontSize="small" />
          </IconButton>
          <IconButton size="small">
            <EditIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  const renderPageMatrix = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Page Matrix: {selectedIndustry?.name || 'Select an industry'}
          </Typography>
          <Box>
            <Button
              startIcon={<RefreshIcon />}
              onClick={handleRunScoringCycle}
              disabled={!selectedIndustry}
              sx={{ mr: 1 }}
            >
              Run Scoring Cycle
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setPageDialog(true)}
              disabled={!selectedIndustry}
            >
              Generate Page
            </Button>
          </Box>
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Paper sx={{ height: 600 }}>
          <DataGrid
            rows={pages}
            columns={pageColumns}
            pageSizeOptions={[25, 50, 100]}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
              sorting: { sortModel: [{ field: 'score', sort: 'desc' }] },
            }}
            disableRowSelectionOnClick
            sx={{ border: 'none' }}
          />
        </Paper>
      </Grid>
    </Grid>
  );

  // ==========================================
  // ALLOCATION TAB
  // ==========================================

  const renderAllocation = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Capital Allocation: {selectedIndustry?.name || 'Select an industry'}
          </Typography>
          <Button
            variant="contained"
            startIcon={<MoneyIcon />}
            onClick={() => setAllocationDialog(true)}
            disabled={!selectedIndustry}
          >
            Run Allocation Cycle
          </Button>
        </Box>
      </Grid>

      {/* Kelly Formula Card */}
      <Grid item xs={12} md={4}>
        <Card>
          <CardHeader title="Kelly Criterion" subheader="Optimal bet sizing" />
          <CardContent>
            <Box sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 1 }}>
              <Typography variant="body2" fontFamily="monospace" textAlign="center">
                f* = (bp - q) / b
              </Typography>
            </Box>
            <List dense sx={{ mt: 2 }}>
              <ListItem>
                <ListItemText primary="f*" secondary="Optimal fraction of capital" />
              </ListItem>
              <ListItem>
                <ListItemText primary="b" secondary="Net odds (return per unit)" />
              </ListItem>
              <ListItem>
                <ListItemText primary="p" secondary="Win probability" />
              </ListItem>
              <ListItem>
                <ListItemText primary="q" secondary="Loss probability (1-p)" />
              </ListItem>
            </List>
          </CardContent>
        </Card>
      </Grid>

      {/* Active Allocations */}
      <Grid item xs={12} md={8}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Active Allocations</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Resource Type</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell align="right">Kelly f*</TableCell>
                  <TableCell align="right">Win Prob</TableCell>
                  <TableCell align="right">Expected Odds</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allocations.map((alloc) => (
                  <TableRow key={alloc.id} hover>
                    <TableCell>{alloc.resourceType}</TableCell>
                    <TableCell align="right">${alloc.amount.toFixed(2)}</TableCell>
                    <TableCell align="right">{(alloc.kellyFraction * 100).toFixed(1)}%</TableCell>
                    <TableCell align="right">{(alloc.winProbability * 100).toFixed(0)}%</TableCell>
                    <TableCell align="right">{alloc.expectedOdds.toFixed(2)}x</TableCell>
                    <TableCell>
                      <Chip
                        label={alloc.status}
                        size="small"
                        color={alloc.status === 'completed' ? 'success' : alloc.status === 'active' ? 'primary' : 'default'}
                      />
                    </TableCell>
                  </TableRow>
                ))}
                {allocations.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="textSecondary">No allocations yet</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Grid>
    </Grid>
  );

  // ==========================================
  // DIALOGS
  // ==========================================

  const renderDialogs = () => (
    <>
      {/* Industry Dialog */}
      <Dialog open={industryDialog} onClose={() => setIndustryDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Industry</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              value={newIndustry.name}
              onChange={(e) => setNewIndustry({ ...newIndustry, name: e.target.value })}
              fullWidth
            />
            <TextField
              label="Description"
              value={newIndustry.description}
              onChange={(e) => setNewIndustry({ ...newIndustry, description: e.target.value })}
              multiline
              rows={3}
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={newIndustry.isRegulated}
                  onChange={(e) => setNewIndustry({ ...newIndustry, isRegulated: e.target.checked })}
                />
              }
              label="Regulated Industry (medical, legal, financial)"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIndustryDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateIndustry}>Create</Button>
        </DialogActions>
      </Dialog>

      {/* Ontology Dialog */}
      <Dialog open={ontologyDialog} onClose={() => setOntologyDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Ontology Item</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={newOntologyItem.type}
                label="Type"
                onChange={(e) => setNewOntologyItem({ ...newOntologyItem, type: e.target.value as any })}
              >
                <MenuItem value="primitive">Primitive (core service)</MenuItem>
                <MenuItem value="scenario">Scenario (use case)</MenuItem>
                <MenuItem value="modifier">Modifier (urgency/quality)</MenuItem>
                <MenuItem value="context">Context (location/time)</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Name"
              value={newOntologyItem.name}
              onChange={(e) => setNewOntologyItem({ ...newOntologyItem, name: e.target.value })}
              placeholder="e.g., Emergency Plumber"
              fullWidth
            />
            <TextField
              label="Slug"
              value={newOntologyItem.slug}
              onChange={(e) => setNewOntologyItem({ ...newOntologyItem, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
              placeholder="e.g., emergency-plumber"
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOntologyDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateOntologyItem}>Create</Button>
        </DialogActions>
      </Dialog>

      {/* Allocation Dialog */}
      <Dialog open={allocationDialog} onClose={() => setAllocationDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Run Allocation Cycle</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Typography variant="body2" color="textSecondary">
              The Kelly Criterion will optimize resource distribution across high-performing pages.
            </Typography>
            <Box>
              <Typography gutterBottom>Budget: ${allocationBudget}</Typography>
              <Slider
                value={allocationBudget}
                onChange={(_, value) => setAllocationBudget(value as number)}
                min={100}
                max={10000}
                step={100}
                marks={[
                  { value: 100, label: '$100' },
                  { value: 5000, label: '$5K' },
                  { value: 10000, label: '$10K' },
                ]}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAllocationDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleRunAllocation}>Allocate</Button>
        </DialogActions>
      </Dialog>
    </>
  );

  // ==========================================
  // MAIN RENDER
  // ==========================================

  if (loading && !dashboardData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header with One-Click Action */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Attention Engine
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Compounding demand capture system • Programmatic SEO • Kelly-optimized allocation
          </Typography>
        </Box>
        
        {/* ONE-CLICK SEO OPTIMIZE - The Power Button */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            size="large"
            color="success"
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <AutoGraphIcon />}
            onClick={async () => {
              try {
                setLoading(true);
                // Run scoring cycle - with industry if selected, global if not
                const result = await attentionEngineService.runScoringCycle(selectedIndustry?.id || '');
                setSuccess(`✓ SEO Optimized! Processed ${result.pagesScored || 0} pages. Average score: ${(result.avgScore || 0).toFixed(1)}`);
                await loadDashboard();
                if (selectedIndustry) await loadIndustryData();
              } catch (err: any) {
                setError(err.message || 'Failed to optimize');
              } finally {
                setLoading(false);
              }
            }}
            disabled={loading}
            sx={{ 
              px: 4, 
              py: 1.5,
              boxShadow: '0 4px 14px rgba(76, 175, 80, 0.4)',
              '&:hover': {
                boxShadow: '0 6px 20px rgba(76, 175, 80, 0.6)',
              }
            }}
          >
            {loading ? 'Optimizing...' : selectedIndustry ? 'Optimize SEO Now' : 'Optimize All SEO'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => {
              loadDashboard();
              if (selectedIndustry) loadIndustryData();
            }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab icon={<DashboardIcon />} label="Global Map" iconPosition="start" />
          <Tab icon={<OntologyIcon />} label="Ontology" iconPosition="start" />
          <Tab icon={<MatrixIcon />} label="Page Matrix" iconPosition="start" />
          <Tab icon={<MoneyIcon />} label="Allocation" iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Loading overlay */}
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Tab Content */}
      <TabPanel value={activeTab} index={0}>{renderGlobalMap()}</TabPanel>
      <TabPanel value={activeTab} index={1}>{renderOntology()}</TabPanel>
      <TabPanel value={activeTab} index={2}>{renderPageMatrix()}</TabPanel>
      <TabPanel value={activeTab} index={3}>{renderAllocation()}</TabPanel>

      {/* Dialogs */}
      {renderDialogs()}

      {/* Notifications */}
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
      </Snackbar>
      <Snackbar open={!!success} autoHideDuration={4000} onClose={() => setSuccess(null)}>
        <Alert severity="success" onClose={() => setSuccess(null)}>{success}</Alert>
      </Snackbar>
    </Box>
  );
};

export default AttentionEnginePage;
